import { useState, useRef, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { CustomFileUpload } from './CustomFileUpload';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function UploadReviewForm({
  reviewedSessionId: reviewedSessionIdProp,
  batchTitle: batchTitleProp,
  onSuccess,
} = {}) {
  const { sessionId: reviewedSessionIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const reviewedSessionId = reviewedSessionIdProp ?? reviewedSessionIdParam;
  const batchTitle = batchTitleProp ?? searchParams.get('title');

  const [sessionId] = useState(() => crypto.randomUUID());
  const { getAccessTokenSilently } = useAuth0();
  const toast = useRef(null);

  const [token, setToken] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [reviewer, setReviewer] = useState('');
  const [saveAsDownloaded, setSaveAsDownloaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Only set page title when used as a standalone page
  useEffect(() => { if (!reviewedSessionIdProp) document.title = 'OptiConfig Upload Review'; }, [reviewedSessionIdProp]);

  useEffect(() => {
    fetchAccessToken(getAccessTokenSilently)
      .then(setToken)
      .catch(() => showToast('error', 'Error', 'Failed to fetch access token'));
  }, [getAccessTokenSilently]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 4000 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uploadedFiles || uploadedFiles.length === 0) {
      showToast('warn', 'Validation', 'Please upload a review file.');
      return;
    }
    if (!reviewer.trim()) {
      showToast('warn', 'Validation', 'Please enter a reviewer name.');
      return;
    }

    setSubmitting(true);
    try {
      const accessToken = await fetchAccessToken(getAccessTokenSilently);

      const fileData = uploadedFiles.map((file) => ({
        relativePath: file._relativePath,
        lastModified: file.lastModified,
        lastModifiedDate: file.lastModifiedDate,
        name: file.name,
        size: file.size,
        type: file.type,
        webkitRelativePath: file.webkitRelativePath,
      }));

      const payload = {
        sessionId,
        reviewedSessionId,
        reviewer: reviewer.trim(),
        uploadedFiles: fileData,
        saveAsDownloaded,
      };

      const res = await fetch(`${API_BASE_URL}/file/review/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) await apiCallErrorHandler(res, 'Failed to submit review');

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/sessions', { state: { toast: { severity: 'success', summary: 'Success', detail: 'Review uploaded successfully.' } } });
      }
    } catch (err) {
      showToast('error', 'Error', err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '0.5rem' }}>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit} className="grid formgrid p-fluid">
        <div className="field col-12">
          <label htmlFor="reviewer" style={{ fontWeight: 500 }}>Reviewer</label>
          <InputText
            id="reviewer"
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            placeholder="Enter reviewer name"
            disabled={submitting}
            className="w-full"
          />
        </div>

        <div className="field col-12" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Checkbox
            inputId="saveAsDownloaded"
            checked={saveAsDownloaded}
            onChange={(e) => setSaveAsDownloaded(e.checked)}
            disabled={submitting}
          />
          <label htmlFor="saveAsDownloaded" style={{ fontWeight: 500, cursor: 'pointer' }}>Save as Downloaded</label>
        </div>

        <div className="field col-12" style={{ marginTop: '0.5rem' }}>
          <label style={{ fontWeight: 500, display: 'block', marginBottom: '0.5rem' }}>Reviewed File</label>
          {token && (
            <CustomFileUpload
              allowMultiple={false}
              acceptedFileTypes={[
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                '.xlsx',
              ]}
              onMessage={(msg) => showToast('error', 'Upload Error', msg.detail)}
              token={token}
              fileValue={uploadedFiles}
              sessionId={sessionId}
              disabled={submitting}
              onChangeFiles={(files) => setUploadedFiles(files)}
            />
          )}
        </div>

        <div className="field col-12" style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Button
            type="submit"
            label="Submit"
            className="p-button-primary"
            style={{ maxWidth: 200, width: '100%' }}
            disabled={submitting || !reviewer.trim() || uploadedFiles.length === 0}
            loading={submitting}
          />
        </div>
      </form>
    </div>
  );
}
